import { authApi } from "@api";
import { FundingProfileType, Select, SelectProps, cn } from "kz-ui-sdk";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdAccountBalance } from "react-icons/md";

interface PaymentAccountTypeSelectProps extends Omit<SelectProps<{ id: string; name: string }>, "label" | "options"> {
  accountType?: FundingProfileType;
}

const PaymentAccountTypeSelect: React.FC<PaymentAccountTypeSelectProps> = ({
  accountType,
  ...props
}: PaymentAccountTypeSelectProps) => {
  const { t } = useTranslation();
  const { data } = authApi.useGetProfileQuery();

  const paymentList = useMemo(() => {
    const bankMap = data?.bankInfos;
    const eWalletMap = data?.ewalletInfos;
    const memberFundingProfileSettings = data?.fundingProfileSettings?.supportedTypes;

    let paymentMap = {
      ...Object.fromEntries(
        Object.entries(bankMap ?? {}).map(([key, value]) => [key, { ...value, type: FundingProfileType.Bank }]),
      ),
      ...Object.fromEntries(
        Object.entries(eWalletMap ?? {}).map(([key, value]) => [key, { ...value, type: FundingProfileType.EWallet }]),
      ),
    };

    // If accountType is provided, filter the paymentMap by the accountType
    if (accountType) {
      paymentMap = Object.fromEntries(
        Object.entries(paymentMap ?? {}).filter(([_, config]) => config.type === accountType),
      );
    }

    return Object.entries(paymentMap ?? {})
      .filter(([_, config]) => !!config.visibility)
      .sort(([, lhsConfig], [, rhsConfig]) => {
        if (lhsConfig.type !== rhsConfig.type) {
          return (
            (memberFundingProfileSettings?.[lhsConfig.type]?.sort ?? 0) -
            (memberFundingProfileSettings?.[rhsConfig.type]?.sort ?? 0)
          );
        }
        return (lhsConfig.sort ?? 0) - (rhsConfig.sort ?? 0);
      })
      .map(([paymentKey, paymentConfig]) => {
        let paymentName = "";

        switch (paymentConfig.type) {
          // Display e-wallet name only: Gcash, Paymaya, etc.
          case FundingProfileType.EWallet:
            paymentName = paymentConfig.name;
            break;
          // Display bank name with bank code: KBank (KBK)
          case FundingProfileType.Bank:
            paymentName = `${paymentConfig.name} ${paymentKey ? `(${paymentKey})` : ""}`;
            break;
          default:
            paymentName = paymentConfig.name;
            break;
        }

        return {
          id: paymentKey,
          name: t(paymentName),
          icon: !!paymentConfig.icon ? (
            <img
              src={paymentConfig.icon}
              alt={paymentConfig.name}
              className={cn(`mt-0.5 h-[24px] w-[24px] shrink-0 self-start object-contain`)}
            />
          ) : (
            <MdAccountBalance
              className="shrink-0 text-content-base"
              size={24}
            />
          ),
        };
      });
  }, [data?.bankInfos, data?.ewalletInfos, data?.fundingProfileSettings, accountType, t]);

  return (
    <Select
      {...props}
      placeholder={t("please select")}
      options={paymentList}
      optionIconPlaceholder={<div className="mt-0.5 h-[24px] w-[24px] flex-shrink-0" />}
      size={"lg"}
      classes={{
        "root&": "gap-0",
        "label&": "text-content-base text-base",
        "placeHolder&": cn("text-content-soft text-left w-56", {
          "text-content-base": !!props.value,
        }),
        "helperText&": "!mt-1 !text-xs my-0 px-0",
        "selectedOption&": "!text-content-base text-base",
        "errorText&": "text-error-base text-xs",
        "option&": "cursor-pointer hover:bg-white hover:bg-opacity-5",
        "optionsContainer&": "divide-stroke-base divide-y",
      }}
      aria-label="Payment account type select"
    />
  );
};

export default PaymentAccountTypeSelect;
